import { Reveal, Bubbles } from "@/components/site/Motion";
import { Btn } from "@/components/site/Btn";
import { ArrowRight } from "lucide-react";
import { PRO, CATEGORIES } from "@/lib/content";
import { ART } from "@/lib/assets";

const accent = { sky: "bg-sky", coral: "bg-coral", seagreen: "bg-seagreen", yellow: "bg-yellow" };
const accentSoft = { sky: "bg-sky/12", coral: "bg-coral/12", seagreen: "bg-seagreen/12", yellow: "bg-yellow/20" };

export default function Professionals() {
  return (
    <>
      <section className="relative bg-gradient-to-b from-sky/30 to-cream pt-16 md:pt-24 pb-12 overflow-hidden" data-testid="pros-hero">
        <Bubbles count={7} />
        <div className="max-w-5xl mx-auto px-6 md:px-10 relative">
          <Reveal>
            <p className="font-body text-sm font-bold uppercase tracking-[0.15em] text-ocean mb-4">The professionals</p>
            <h1 className="font-display font-extrabold text-5xl md:text-7xl text-navy leading-[0.95]">{PRO.hero}</h1>
            <p className="font-body text-lg md:text-xl text-navy/70 mt-6 max-w-2xl">{PRO.heroSub}</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-8" data-testid="pros-categories">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-6 md:gap-8">
          {CATEGORIES.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.key} delay={i * 0.08}>
                <div className={`h-full ${accentSoft[c.color]} border-2 border-navy rounded-3xl p-8 shadow-brutal flex flex-col`} data-testid={`pro-category-${c.key}`}>
                  <div className="flex items-center gap-4">
                    <span className={`inline-grid place-items-center w-14 h-14 rounded-2xl ${accent[c.color]} border-2 border-navy`}><Icon className="w-7 h-7 text-navy" /></span>
                    <h2 className="font-display font-bold text-2xl md:text-3xl text-navy">{c.title}</h2>
                  </div>
                  <p className="font-body text-navy/70 mt-4">{c.blurb}</p>
                  <ul className="mt-5 space-y-2 flex-1">
                    {c.roles.map((r) => (
                      <li key={r} className="flex items-center gap-2 font-body text-navy/80"><span className="text-ocean font-bold">→</span> {r}</li>
                    ))}
                  </ul>
                  <div className="mt-7"><Btn to="/contact" variant="white" testid={`pro-cta-${c.key}`} className="!py-2.5 !text-sm">Find this type of professional <ArrowRight className="w-4 h-4" /></Btn></div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="pros-visual-resume">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <img src={ART.candidateProfile} alt="A candidate presentation showing a video interview, skills, experience and screening notes" className="w-full max-w-md rounded-[32px] border-2 border-navy shadow-brutal-lg bg-white" loading="lazy" />
          </Reveal>
          <Reveal delay={0.12}>
            <h2 className="font-display font-bold text-4xl md:text-5xl text-navy leading-[1.05]">You see more than a resume.</h2>
            <p className="font-body text-lg text-navy/75 mt-5">Before you spend time interviewing anyone, you get a fuller picture. Our candidate presentation can include a recorded video interview, screening notes, relevant experience and skills, so you understand the person behind the resume.</p>
            <p className="font-display font-bold text-xl text-navy mt-6 bg-yellow inline-block px-4 py-2 rounded-xl border-2 border-navy shadow-brutal -rotate-1">We shortlist. You decide.</p>
            <div className="mt-8"><Btn to="/how-it-works" variant="sky" testid="pros-how-cta">See how it works <ArrowRight className="w-5 h-5" /></Btn></div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
